import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, tap, catchError } from 'rxjs';

/**
 * Tenant configuration from the Worker's /config.json endpoint.
 */
export interface TenantConfig {
  slug: string;
}

/**
 * Service to fetch and cache tenant configuration from the edge Worker.
 *
 * The Worker serves /config.json which contains the tenant slug from KV.
 * If the tenant doesn't exist in KV, the Worker returns 404 and we redirect to /not-found.
 */
@Injectable({
  providedIn: 'root',
})
export class TenantConfigService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private config: TenantConfig | null = null;
  private configChecked = false;

  /**
   * Fetches the tenant configuration from /config.json.
   * If the fetch fails with 404, redirects to /not-found.
   *
   * @returns Observable<TenantConfig | null> - null if tenant not found
   */
  fetchConfig(): Observable<TenantConfig | null> {
    // Skip if already checked and tenant exists
    if (this.configChecked && this.config) {
      return of(this.config);
    }

    // Skip config check for certain paths
    const path = window.location.pathname;
    if (path.startsWith('/preview') || path.startsWith('/not-found')) {
      this.configChecked = true;
      return of(null);
    }

    return this.http.get<TenantConfig>('/config.json').pipe(
      tap((config) => {
        this.config = config;
        this.configChecked = true;
      }),
      catchError((error: HttpErrorResponse) => {
        this.configChecked = true;

        if (error.status === 404) {
          // Tenant not found - redirect to not-found page
          console.warn('Tenant not found (404 from /config.json), redirecting to /not-found');
          this.router.navigate(['/not-found'], { replaceUrl: true });
        } else {
          // Other error - log but don't redirect (might be network issue)
          console.error('Failed to fetch tenant config:', error);
        }

        return of(null);
      })
    );
  }

  /**
   * Gets the cached tenant configuration, or null if not yet fetched or not found.
   */
  getConfig(): TenantConfig | null {
    return this.config;
  }

  /**
   * Gets the tenant slug from the cached config.
   */
  getSlug(): string | null {
    return this.config?.slug ?? null;
  }

  /**
   * Returns true if the config has been checked (successfully or not).
   */
  hasCheckedConfig(): boolean {
    return this.configChecked;
  }
}
